import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { SetupStep } from "@/components/SetupStep";
import { useConfirm } from "@/components/ConfirmDialog";
import { TextInput } from "@/components/TextInput";
import { Toast, useToast } from "@/components/Toast";
import { LoadError } from "@/components/LoadError";
import { StatusPill } from "@/components/StatusPill";
import { SettingsSection, SettingsSkeleton } from "./SettingsSection";
import { useApi } from "@/lib/api";
import { isConfigured, isEnabled } from "@/lib/enums";
import { formatRelativeTime } from "@/lib/formatRelativeTime";

interface LogStreamStatus {
  status: number;
  webhookUrl: string | null;
  format: string | null;
  authHeaderName: string | null;
  lastDeliveredAt: string | null;
  lastFailedAt: string | null;
  lastFailure: string | null;
}

function isFailing(status: LogStreamStatus): boolean {
  return (
    status.lastFailedAt !== null &&
    (status.lastDeliveredAt === null ||
      new Date(status.lastFailedAt) >= new Date(status.lastDeliveredAt))
  );
}

const formatOptions = [
  {
    value: "Generic",
    label: "Generic",
    description: "Flat JSON. Elastic, Honeycomb, New Relic, and Sumo Logic.",
  },
  {
    value: "Datadog",
    label: "Datadog",
    description: "Array-wrapped JSON.",
  },
  {
    value: "Loki",
    label: "Grafana Loki",
    description: "Streams JSON.",
  },
  {
    value: "Seq",
    label: "Seq",
    description: "CLEF.",
  },
];

export function LogStreaming() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { toast, showToast } = useToast();
  const { orgId } = useParams();
  const [status, setStatus] = useState<LogStreamStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [isEditing, setIsEditing] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [format, setFormat] = useState("Generic");
  const [authHeaderName, setAuthHeaderName] = useState("");
  const [authHeaderValue, setAuthHeaderValue] = useState("");

  const fetchStatus = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    return api.get<LogStreamStatus>(`/organizations/${orgId}/log-streaming`);
  }, [accessToken, api, orgId]);

  const applyStatus = useCallback((data: LogStreamStatus | null) => {
    if (data) {
      setStatus(data);
      setError(null);
    } else {
      setError("Couldn't load log streaming status.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchStatus().then((data) => {
      if (!cancelled) {
        applyStatus(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchStatus, applyStatus]);

  async function handleConfigure(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/log-streaming`,
        "POST",
        {
          webhookUrl,
          format,
          authHeaderName: authHeaderName || null,
          authHeaderValue: authHeaderValue || null,
        },
        "Couldn't configure log streaming.",
      );

      setWebhookUrl("");
      setFormat("Generic");
      setAuthHeaderName("");
      setAuthHeaderValue("");
      setIsEditing(false);
      applyStatus(await fetchStatus());
      showToast("Log streaming configured.", "success");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't configure log streaming.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit() {
    if (!status) {
      return;
    }

    setWebhookUrl(status.webhookUrl ?? "");
    setFormat(status.format ?? "Generic");
    setAuthHeaderName(status.authHeaderName ?? "");
    setAuthHeaderValue("");
    setError(null);
    setIsEditing(true);
  }

  function cancelEdit() {
    setIsEditing(false);
    setError(null);
  }

  async function toggleEnabled() {
    if (!accessToken || !orgId || !status) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/log-streaming/${isEnabled(status.status) ? "disable" : "enable"}`,
        "PATCH",
        undefined,
        "Couldn't update log streaming status.",
      );

      applyStatus(await fetchStatus());
      showToast(
        isEnabled(status.status)
          ? "Log streaming disabled."
          : "Log streaming enabled.",
        "success",
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't update log streaming status.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const [testState, setTestState] = useState<"idle" | "ok" | "failed">("idle");

  async function handleSendTest() {
    if (!orgId) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/log-streaming/test`,
        "POST",
        undefined,
        "The test event could not be delivered.",
      );

      setTestState("ok");
      showToast("Test event delivered.", "success");
    } catch (err) {
      setTestState("failed");
      setError(
        err instanceof Error
          ? err.message
          : "The test event could not be delivered.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!accessToken || !orgId) {
      return;
    }

    const confirmed = await confirm(
      "Are you sure you want to delete this integration?",
      {
        title: "Delete integration",
        confirmLabel: "Delete",
      },
    );

    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.send(
        `/organizations/${orgId}/log-streaming`,
        "DELETE",
        undefined,
        "Couldn't delete log streaming integration.",
      );

      applyStatus(await fetchStatus());
      showToast("Log streaming integration deleted.", "success");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't delete log streaming integration.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SettingsSection
      className="text-foreground"
      title="Log streaming"
      description="Stream audit logs to your logging platform."
    >
      {!status && error ? (
        <LoadError
          what="log streaming"
          onRetry={() => void fetchStatus().then(applyStatus)}
        />
      ) : !status ? (
        <SettingsSkeleton />
      ) : (
        <>
          {error && (
            <p className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="rounded-lg bg-surface p-4">
            <ol className="list-none">
              <SetupStep
                label="1"
                state={
                  isConfigured(status.status) && !isEditing ? "done" : "current"
                }
                title="Configure log destination"
              >
                {!isConfigured(status.status) || isEditing ? (
                  <form onSubmit={handleConfigure} className="mt-3 space-y-4">
                    <div>
                      <label
                        htmlFor="webhookUrl"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Destination URL
                      </label>
                      <TextInput
                        type="url"
                        id="webhookUrl"
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://api.example.com/v1/logs"
                        required

                        className="w-full"
                      />
                    </div>
                    <fieldset>
                      <legend className="mb-1.5 block text-sm text-foreground">
                        Format
                      </legend>
                      <div className="space-y-2">
                        {formatOptions.map((option) => (
                          <label
                            key={option.value}
                            className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${
                              format === option.value
                                ? "border-accent bg-surface/40"
                                : "border-muted bg-surface/40 hover:border-accent/50"
                            }`}
                          >
                            <input
                              type="radio"
                              name="logStreamFormat"
                              value={option.value}
                              checked={format === option.value}
                              onChange={() => setFormat(option.value)}
                              className="mt-1 h-4 w-4 shrink-0 accent-accent"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium">
                                {option.label}
                              </span>
                              <span className="mt-0.5 block text-xs text-foreground/70">
                                {option.description}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <div>
                      <label
                        htmlFor="authHeaderName"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Auth header name (optional)
                      </label>
                      <TextInput
                        type="text"
                        id="authHeaderName"
                        value={authHeaderName}
                        onChange={(e) => setAuthHeaderName(e.target.value)}
                        placeholder="X-API-KEY"

                        className="w-full"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="authHeaderValue"
                        className="mb-1.5 block text-sm text-foreground"
                      >
                        Auth header value (optional)
                      </label>
                      <TextInput
                        type="password"
                        id="authHeaderValue"
                        value={authHeaderValue}
                        onChange={(e) => setAuthHeaderValue(e.target.value)}
                        placeholder={
                          isEditing && status.authHeaderName
                            ? "················"
                            : undefined
                        }

                        className="w-full"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? "Saving..." : "Save"}
                      </Button>
                      {isEditing && (
                        <Button
                          variant="secondary"
                          onClick={cancelEdit}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                ) : (
                  <>
                    <dl className="mt-1 space-y-1">
                      <div>
                        <dt className="inline text-sm text-foreground/70">
                          Destination:{" "}
                        </dt>
                        <dd className="inline text-sm">{status.webhookUrl}</dd>
                      </div>
                      <div>
                        <dt className="inline text-sm text-foreground/70">
                          Format:{" "}
                        </dt>
                        <dd className="inline text-sm">
                          {formatOptions.find(
                            (option) => option.value === status.format,
                          )?.label ?? status.format}
                        </dd>
                      </div>
                      {status.authHeaderName && (
                        <div>
                          <dt className="inline text-sm text-foreground/70">
                            Auth header:{" "}
                          </dt>
                          <dd className="inline text-sm">
                            {status.authHeaderName}: ················
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Button
                        variant="secondary"
                        onClick={startEdit}
                        disabled={submitting}
                      >
                        Edit destination
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleSendTest}
                        disabled={submitting}
                      >
                        {submitting ? "Sending..." : "Send test event"}
                      </Button>
                    </div>
                    {testState !== "idle" && (
                      <p
                        className={`mt-2 text-sm ${
                          testState === "ok" ? "text-success" : "text-danger"
                        }`}
                      >
                        {testState === "ok"
                          ? "Your destination accepted a test event."
                          : "The test event was not delivered. Check the destination and try again."}
                      </p>
                    )}
                  </>
                )}
              </SetupStep>

              <SetupStep
                label="2"
                state={
                  !isConfigured(status.status)
                    ? "todo"
                    : isEnabled(status.status)
                      ? "done"
                      : "current"
                }
                title="Enable log streaming"
                last
              >
                <p className="mt-0.5 mb-3 text-sm text-foreground/70">
                  {!isConfigured(status.status)
                    ? "Tendra will stream audit logs to your configured destination."
                    : isEnabled(status.status)
                      ? "Audit logs are being streamed to your configured destination."
                      : "Tendra will stream audit logs to your configured destination."}
                </p>
                {isEnabled(status.status) && (
                  <>
                    {isFailing(status) ? (
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                        <StatusPill variant="offline">Failing</StatusPill>
                        <span className="text-foreground/70">
                          {status.lastFailure}
                        </span>
                      </div>
                    ) : (
                      status.lastDeliveredAt && (
                        <div className="mb-2 text-sm">
                          <StatusPill variant="active">Delivering</StatusPill>
                        </div>
                      )
                    )}
                    <dl className="mb-3 space-y-1 text-sm">
                      <div>
                        <dt className="inline text-foreground/70">
                          Last delivered:{" "}
                        </dt>
                        <dd className="inline">
                          {status.lastDeliveredAt
                            ? formatRelativeTime(status.lastDeliveredAt)
                            : "Not yet"}
                        </dd>
                      </div>
                    </dl>
                  </>
                )}
                {isConfigured(status.status) && (
                  <Button
                    variant="secondary"
                    onClick={toggleEnabled}
                    disabled={submitting}
                  >
                    {isEnabled(status.status) ? "Disable" : "Enable"}
                  </Button>
                )}
              </SetupStep>
            </ol>
          </div>

          {isConfigured(status.status) && (
            <div className="mt-4 flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Delete this integration
                </h3>
                <p className="mt-0.5 max-w-md text-xs text-foreground/70">
                  Stops streaming audit logs to your configured destination.
                  Events already delivered are unaffected.
                </p>
              </div>
              <Button
                variant="dangerOutline"
                onClick={handleDelete}
                disabled={submitting}
                className="shrink-0"
              >
                Delete integration
              </Button>
            </div>
          )}
        </>
      )}

      {confirmDialog}
      <Toast toast={toast} />
    </SettingsSection>
  );
}
